import { PropsWithChildren, useMemo } from 'react'

import { I18nextProvider, useTranslation } from 'react-i18next'

/**
 * Renders a journey's client copy in `locale` instead of the customer's locale, for journeys that
 * set `scope.localeOverride`.
 *
 * The clone shares the parent instance's resource store and backend, and opts out of suspense so a
 * blocker renders the English source string rather than suspending while the bundle loads.
 */
export const JourneyLocaleProvider = ({ locale, children }: PropsWithChildren<{ locale?: string }>) => {
  const { i18n } = useTranslation()

  const pinnedI18n = useMemo(() => {
    if (locale == null || i18n?.cloneInstance == null || i18n.language === locale) {
      return undefined
    }

    return i18n.cloneInstance({ lng: locale, react: { useSuspense: false } })
  }, [i18n, locale])

  // Most journeys pin nothing, and the rendering path for those must not reference
  // `I18nextProvider` — plenty of projects stub react-i18next without that export, and reading a
  // missing export off a vitest mock throws.
  if (pinnedI18n == null) {
    return children
  }

  return <I18nextProvider i18n={pinnedI18n}>{children}</I18nextProvider>
}
